import { CATEGORY_FALLBACK_IMAGES } from "./aa-catalog-images";

const DEFAULT_PRODUCT_IMAGE = CATEGORY_FALLBACK_IMAGES.Bebe;

export const ITEMS_PER_DOZEN = 12;
export const DEFAULT_DOZEN_PER_BOX = 25;

/** @deprecated Use DEFAULT_DOZEN_PER_BOX */
export const PACKAGES_PER_BOX = DEFAULT_DOZEN_PER_BOX;

/** @deprecated Order step is always 1 box */
export const ORDER_STEP_PACKAGES = DEFAULT_DOZEN_PER_BOX;

export function getItemsPerDozen(product) {
  const raw =
    product?.items_per_dozen ??
    product?.items_per_package ??
    product?.pack_size ??
    product?.packSize ??
    ITEMS_PER_DOZEN;
  const parsed = Number.parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : ITEMS_PER_DOZEN;
}

export function getDozenPerBox(product) {
  const raw =
    product?.dozen_per_box ?? product?.packages_per_box ?? product?.order_step_packages ?? DEFAULT_DOZEN_PER_BOX;
  const parsed = Number.parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DOZEN_PER_BOX;
}

export function getItemsPerBox(product) {
  const fromApi = Number(product?.items_per_box ?? product?.order_step_items);
  if (Number.isFinite(fromApi) && fromApi > 0) {
    return fromApi;
  }

  return getDozenPerBox(product) * getItemsPerDozen(product);
}

/** Cart/order quantity step in items (one whole box). */
export function getOrderStepItems(product) {
  return getItemsPerBox(product);
}

/** @deprecated Use getOrderStepItems for cart increments. */
export function getPackSize(product) {
  return getOrderStepItems(product);
}

export function boxesToItems(boxes, product) {
  return Math.max(0, Number(boxes) || 0) * getItemsPerBox(product);
}

export function itemsToBoxes(items, product) {
  const itemsPerBox = getItemsPerBox(product);
  if (!items || items <= 0 || itemsPerBox <= 0) {
    return 0;
  }

  return Math.floor(Number(items) / itemsPerBox);
}

/** @deprecated Use boxesToItems */
export function packagesToItems(packages, product) {
  return boxesToItems(packages, product);
}

/** @deprecated Use itemsToBoxes */
export function itemsToPackages(items, product) {
  return itemsToBoxes(items, product);
}

/** @deprecated Use getDozenPerBox */
export function getPackagesPerBox(product) {
  return getDozenPerBox(product);
}

/** @deprecated Use getDozenPerBox; one box is the order step */
export function getOrderStepPackages(product) {
  return getDozenPerBox(product);
}

/** @deprecated Use getItemsPerDozen */
export function getItemsPerPackage(product) {
  return getItemsPerDozen(product);
}

export function maxOrderableItems(stockItems, product) {
  const boxes = itemsToBoxes(stockItems, product);
  if (boxes < 1) {
    return 0;
  }

  return boxesToItems(boxes, product);
}

/** Reseller wholesale orders use catalog product stock, not per-variant stock. */
export function getOrderableStock(product, variantStock) {
  if (product?.wholesaleCatalog) {
    return Number(product?.stock_quantity ?? product?.stock ?? 0);
  }

  if (product?.variation?.length) {
    return Number(variantStock ?? 0);
  }

  return Number(product?.stock_quantity ?? product?.stock ?? 0);
}

export function isProductOrderable(product, variantStock) {
  return maxOrderableItems(getOrderableStock(product, variantStock), product) > 0;
}

export function requiresVariantSelection(product) {
  return Boolean(product?.variation?.length) && !product?.wholesaleCatalog;
}

export function packagingInfoLabel(product) {
  const itemsPerDozen = getItemsPerDozen(product);
  const dozenPerBox = getDozenPerBox(product);
  const itemsPerBox = getItemsPerBox(product);

  return `${itemsPerDozen} items per dozen · ${dozenPerBox} dozen per box · sold by box (${itemsPerBox} items)`;
}

/** Convert item count to whole dozens (may include partial dozen). */
export function itemsToDozens(items, product) {
  const itemsPerDozen = getItemsPerDozen(product);
  if (!items || items <= 0 || itemsPerDozen <= 0) {
    return 0;
  }

  return Math.floor(Number(items) / itemsPerDozen);
}

/**
 * Human-readable stock summary for B2B UI.
 * @returns {{ boxes: number, dozens: number, orderable: boolean, totalDozens?: number, availableDozens?: number, inProductionDozens?: number }}
 */
export function getStockSummary(product, stockItems, options = {}) {
  const stock = Number(stockItems ?? 0);
  const boxes = itemsToBoxes(stock, product);
  const dozens = itemsToDozens(stock, product);
  const dozenPerBox = getDozenPerBox(product);

  const summary = {
    boxes,
    dozens,
    dozenPerBox,
    orderable: boxes >= 1
  };

  if (options.includeInventoryBreakdown && product) {
    const totalItems = Number(product.inventory_total_quantity ?? product.stock_quantity ?? stock);
    const availableItems = Number(
      product.inventory_available_for_immediate_fulfillment_quantity ?? product.stock_quantity ?? stock
    );
    const inProductionItems = Number(
      product.inventory_in_production_quantity ?? Math.max(0, totalItems - availableItems)
    );
    summary.totalDozens = itemsToDozens(totalItems, product);
    summary.availableDozens = itemsToDozens(availableItems, product);
    summary.inProductionDozens = itemsToDozens(inProductionItems, product);
    summary.availableBoxes = itemsToBoxes(availableItems, product);
  }

  return summary;
}

export function packagingInfoLabelI18n(product, t) {
  const dozenPerBox = getDozenPerBox(product);
  return t("packaging_sold_by_box", { dozenPerBox });
}

export function nextPackQty(current, step) {
  const pack = Math.max(1, Number(step) || getOrderStepItems());
  const qty = Math.max(0, Number(current) || 0);
  return qty + pack;
}

export function roundToPack(qty, step) {
  const pack = Math.max(1, Number(step) || getOrderStepItems());
  const value = Math.max(pack, Number(qty) || pack);
  return Math.ceil(value / pack) * pack;
}

export function normalizeShopCategory(category) {
  return {
    ...category,
    name: category?.name || ""
  };
}

export function buildVariationFromVariants(variants = []) {
  const byColor = {};
  variants.forEach((variant) => {
    const color = variant?.color || "Default";
    if (!byColor[color]) {
      byColor[color] = { color, image: "", size: [] };
    }
    byColor[color].size.push({
      name: variant?.size || variant?.label || "One",
      stock: Number(variant?.quantity ?? 0),
      variantId: variant?.id,
      price: variant?.price != null ? Number(variant.price) : null
    });
  });
  return Object.values(byColor);
}

const PLACEHOLDER_COLOR_NAMES = new Set(["", "default", "none", "n/a", "na"]);

export function hasMeaningfulColors(variation) {
  if (!Array.isArray(variation) || variation.length === 0) {
    return false;
  }
  if (variation.length > 1) {
    return true;
  }
  const onlyColor = String(variation[0]?.color ?? "").trim().toLowerCase();
  return !PLACEHOLDER_COLOR_NAMES.has(onlyColor);
}

export function normalizeShopProduct(product, { canBuy = false, wholesaleCatalog = false } = {}) {
  const category = Array.isArray(product?.category)
    ? product.category.filter(Boolean)
    : product?.category
      ? [product.category]
      : [];
  const price = product?.price !== undefined && product?.price !== null
    ? Number(product.price)
    : null;
  const stock =
    product?.inventory_available_for_immediate_fulfillment_quantity !== undefined &&
    product?.inventory_available_for_immediate_fulfillment_quantity !== null
      ? Number(product.inventory_available_for_immediate_fulfillment_quantity)
      : Number(product?.stock_quantity ?? product?.stock ?? 0);
  const galleryImageUrls = Array.isArray(product?.gallery_image_urls)
    ? product.gallery_image_urls.filter(Boolean)
    : [];
  const imageUrl =
    product?.thumbnail_image_url ||
    galleryImageUrls[0] ||
    CATEGORY_FALLBACK_IMAGES[category[0]] ||
    DEFAULT_PRODUCT_IMAGE;
  const imageUrls = [imageUrl, ...galleryImageUrls.filter((url) => url !== imageUrl)];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const variation = variants.length > 0 ? buildVariationFromVariants(variants) : product?.variation;
  const itemsPerDozen = getItemsPerDozen(product);
  const dozenPerBox = getDozenPerBox(product);
  const orderStepItems = getOrderStepItems(product);

  return {
    ...product,
    id: product?.id,
    sku: product?.sku || "",
    name: product?.name || "",
    price,
    discount: 0,
    new: product?.status === "active",
    rating: 0,
    saleCount: 0,
    category,
    tag: category,
    stock,
    stock_quantity: stock,
    items_per_dozen: itemsPerDozen,
    dozen_per_box: dozenPerBox,
    items_per_box: getItemsPerBox(product),
    order_step_boxes: 1,
    order_step_items: orderStepItems,
    items_per_package: itemsPerDozen,
    packages_per_box: dozenPerBox,
    order_step_packages: dozenPerBox,
    pack_size: itemsPerDozen,
    packSize: orderStepItems,
    variants,
    variation,
    image: imageUrls,
    shortDescription: product?.description || "",
    fullDescription: product?.description || "",
    canBuy: canBuy && price !== null,
    wholesaleCatalog
  };
}
